import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CalendarDays, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function LoginPage() {
  const { t } = useTranslation();
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [name, setName] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && isAuthenticated) navigate("/");
  }, [authLoading, isAuthenticated, navigate]);

  const validateEmail = (value) => {
    if (!value) { setEmailError(""); return; }
    setEmailError(EMAIL_RE.test(value) ? "" : t("auth_email_invalid"));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) {
      setEmailError(t("auth_email_invalid"));
      return;
    }
    if (isRegister && password !== passwordConfirm) {
      toast.error(t("auth_password_mismatch"));
      return;
    }
    setLoading(true);
    try {
      if (isRegister) {
        await register(name, email, password);
        toast.success(t("auth_success_register"));
      } else {
        await login(email, password);
        toast.success(t("auth_success_login"));
      }
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("auth_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#020617] px-4" data-testid="login-page">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-violet-500/5 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10 animate-fade-in">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-indigo-400" />
            </div>
            <h1 className="text-2xl font-bold text-white font-heading tracking-tight">Book Flowy</h1>
          </div>
          <LanguageSwitcher variant="widget" />
        </div>

        <div className="bg-slate-950/70 backdrop-blur-xl border border-white/10 rounded-xl p-8">
          <h2 className="text-xl font-semibold text-white font-heading mb-1">
            {isRegister ? t("auth_create") : t("auth_welcome")}
          </h2>
          <p className="text-slate-400 text-sm mb-6">
            {isRegister ? t("auth_create_sub") : t("auth_signin_sub")}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">{t("auth_business_name")}</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("auth_business_placeholder")} required={isRegister}
                  className="bg-slate-900/50 border-slate-700 focus:border-indigo-500" data-testid="register-name-input" />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">{t("email")}</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); validateEmail(e.target.value); }}
                placeholder={t("auth_email_placeholder")}
                required
                className={`bg-slate-900/50 border-slate-700 focus:border-indigo-500 ${emailError ? "border-red-500" : ""}`}
                data-testid="login-email-input"
              />
              {emailError && <p className="text-xs text-red-400">{emailError}</p>}
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">{t("auth_password")}</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("auth_password_placeholder")} required
                className="bg-slate-900/50 border-slate-700 focus:border-indigo-500" data-testid="login-password-input" />
            </div>
            {isRegister && (
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">{t("auth_password_confirm")}</Label>
                <Input
                  type="password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  placeholder={t("auth_password_confirm_placeholder")}
                  required={isRegister}
                  className={`bg-slate-900/50 border-slate-700 focus:border-indigo-500 ${passwordConfirm && password !== passwordConfirm ? "border-red-500" : ""}`}
                />
                {passwordConfirm && password !== passwordConfirm && (
                  <p className="text-xs text-red-400">{t("auth_password_mismatch")}</p>
                )}
              </div>
            )}
            <Button type="submit" disabled={loading}
              className="w-full bg-indigo-500 hover:bg-indigo-600 text-white shadow-[0_0_15px_rgba(99,102,241,0.3)] transition-all active:scale-[0.98]"
              data-testid="login-submit-button">
              {loading ? t("auth_signing_in") : isRegister ? t("auth_create") : t("auth_welcome")}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>

          <div className="mt-6 text-center space-y-2">
            <button onClick={() => setIsRegister(!isRegister)} className="text-sm text-slate-400 hover:text-indigo-400 transition-colors block w-full" data-testid="toggle-auth-mode">
              {isRegister ? t("auth_toggle_login") : t("auth_toggle_register")}
            </button>
            {!isRegister && (
              <a href="/forgot-password" className="text-sm text-slate-500 hover:text-indigo-400 transition-colors">
                {t("auth_forgot_password")}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
